/**
 * Bareun.ai API integration service for spell checking
 * Handles API communication, batch processing, and retry logic
 */

import type {
  GrammarCheckSettings,
  SpellCheckResult,
  Correction,
  BatchProgress,
  BareunApiResponse,
  MorphemeResponse,
  GrammarCheckError,
  GrammarCheckErrorType
} from '../types/grammar';
import { splitIntoChunks, type TextChunk } from '../utils/textProcessing';

/**
 * Default API configuration
 */
const DEFAULT_API_CONFIG = {
  apiKey: import.meta.env.VITE_DEFAULT_API_KEY || '', // No default API key - proxy handles authentication
  apiHost: import.meta.env.VITE_DEFAULT_API_HOST || 'proxy', // Use proxy server
  apiPort: parseInt(import.meta.env.VITE_DEFAULT_API_PORT || '443')
};

/**
 * API request configuration
 */
const API_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // Base delay in ms
  TIMEOUT: 30000, // 30 seconds
  BATCH_DELAY: 500, // Delay between batches
  MAX_CHUNK_SIZE: 4500
};

/**
 * Spell check API service
 * Integrates with Bareun.ai API for Korean grammar checking
 */
export class SpellCheckApiService {
  private morphemeCache: Map<string, MorphemeResponse> = new Map();
  private readonly maxCacheSize = 100;
  private activeRequests = new Set<string>();

  /**
   * Check spelling using Bareun.ai API
   * @param text - Text to check
   * @param settings - Grammar check settings
   * @param progressCallback - Optional progress callback
   * @returns Spell check result
   */
  async checkSpelling(
    text: string,
    settings: GrammarCheckSettings,
    progressCallback?: (progress: BatchProgress) => void
  ): Promise<SpellCheckResult> {
    if (!text.trim()) {
      return { resultOutput: text, corrections: [] };
    }

    // Determine API configuration
    const apiConfig = this.getApiConfig(settings);
    
    // Check for duplicate request
    const requestKey = this.generateRequestKey(text);
    if (this.activeRequests.has(requestKey)) {
      throw this.createError(
        'INVALID_INPUT' as GrammarCheckErrorType,
        'Duplicate request in progress',
        false
      );
    }

    this.activeRequests.add(requestKey);

    try {
      // Check if batch processing is needed
      if (text.length > API_CONFIG.MAX_CHUNK_SIZE) {
        return await this.processBatch(text, apiConfig, progressCallback);
      } else {
        return await this.processSingle(text, apiConfig);
      }
    } finally {
      this.activeRequests.delete(requestKey);
    }
  }

  /**
   * Analyze morphemes in text
   * @param text - Text to analyze
   * @param settings - Grammar check settings
   * @returns Morpheme analysis result
   */
  async analyzeMorphemes(
    text: string,
    settings: GrammarCheckSettings
  ): Promise<MorphemeResponse | null> {
    // Check cache first
    const cacheKey = this.generateRequestKey(text);
    if (this.morphemeCache.has(cacheKey)) {
      return this.morphemeCache.get(cacheKey)!;
    }

    const apiConfig = this.getApiConfig(settings);
    
    try {
      const response = await this.makeApiRequest(
        '/api/v3/morpheme',
        { text },
        apiConfig
      );

      // Cache the result
      const typedResponse = response as MorphemeResponse;
      this.morphemeCache.set(cacheKey, typedResponse);
      this.maintainCacheSize();

      return response as MorphemeResponse;
    } catch (error) {
      console.error('Morpheme analysis failed:', error);
      return null;
    }
  }

  /**
   * Process text in a single API call
   * @param text - Text to process
   * @param apiConfig - API configuration
   * @returns Spell check result
   */
  private async processSingle(
    text: string,
    apiConfig: ApiConfiguration
  ): Promise<SpellCheckResult> {
    try {
      const response = await this.makeApiRequest<BareunApiResponse>(
        '/api/v3/spellcheck',
        { text },
        apiConfig
      );

      return this.parseApiResponse(response);
    } catch (error) {
      console.error('Single processing failed:', error);
      throw error;
    }
  }

  /**
   * Process text in batches
   * @param text - Text to process
   * @param apiConfig - API configuration
   * @param progressCallback - Progress callback
   * @returns Combined spell check result
   */
  private async processBatch(
    text: string,
    apiConfig: ApiConfiguration,
    progressCallback?: (progress: BatchProgress) => void
  ): Promise<SpellCheckResult> {
    const chunks = splitIntoChunks(text, API_CONFIG.MAX_CHUNK_SIZE);
    const results: SpellCheckResult[] = [];
    
    // Notify batch start
    if (progressCallback) {
      progressCallback({
        type: 'batch-start',
        totalChunks: chunks.length
      });
    }

    // Process each chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      
      try {
        // Process chunk
        const result = await this.processSingle(chunk.text, apiConfig);
        
        // Adjust correction positions based on chunk offset
        const adjustedResult = this.adjustCorrectionsForChunk(
          result,
          chunk
        );
        
        results.push(adjustedResult);

        // Update progress
        if (progressCallback) {
          progressCallback({
            type: 'batch-progress',
            current: i + 1,
            total: chunks.length,
            percentage: Math.round(((i + 1) / chunks.length) * 100)
          });
        }

        // Delay between batches to avoid rate limiting
        if (i < chunks.length - 1) {
          await this.delay(API_CONFIG.BATCH_DELAY);
        }
      } catch (error) {
        // Report batch error
        if (progressCallback) {
          progressCallback({
            type: 'batch-error',
            current: i + 1,
            total: chunks.length,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
        throw error;
      }
    }

    // Notify batch complete
    if (progressCallback) {
      progressCallback({
        type: 'batch-complete',
        total: chunks.length
      });
    }

    // Combine results
    return this.combineResults(results);
  }

  /**
   * Make an API request with retry logic
   * @param endpoint - API endpoint
   * @param data - Request data
   * @param apiConfig - API configuration
   * @returns API response
   */
  private async makeApiRequest<T>(
    endpoint: string,
    data: any,
    apiConfig: ApiConfiguration
  ): Promise<T> {
    const url = `${apiConfig.protocol}://${apiConfig.apiHost}:${apiConfig.apiPort}${endpoint}`;
    
    for (let attempt = 0; attempt < API_CONFIG.MAX_RETRIES; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          API_CONFIG.TIMEOUT
        );

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiConfig.apiKey}`
          },
          body: JSON.stringify(data),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw this.handleApiError(response.status, await response.text());
        }

        return await response.json();
      } catch (error) {
        // Check if retryable
        if (attempt < API_CONFIG.MAX_RETRIES - 1) {
          const delay = API_CONFIG.RETRY_DELAY * Math.pow(2, attempt);
          await this.delay(delay);
          continue;
        }
        
        // Final attempt failed
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            throw this.createError(
              'TIMEOUT' as GrammarCheckErrorType,
              'Request timed out',
              true
            );
          }
        }
        
        throw error;
      }
    }

    throw this.createError(
      'NETWORK_ERROR' as GrammarCheckErrorType,
      'Max retries exceeded',
      true
    );
  }

  /**
   * Parse Bareun API response into SpellCheckResult
   * @param response - API response
   * @returns Spell check result
   */
  private parseApiResponse(response: BareunApiResponse): SpellCheckResult {
    const corrections: Correction[] = [];
    
    if (response.revisedSentences) {
      response.revisedSentences.forEach(sentence => {
        if (sentence.revisedBlocks) {
          sentence.revisedBlocks.forEach(block => {
            const correction: Correction = {
              original: block.origin.content,
              corrected: block.revisions.map(r => r.revised),
              help: block.revisions[0]?.comment || 'Grammar correction suggested',
              position: {
                start: block.origin.beginOffset,
                end: block.origin.beginOffset + block.origin.length
              }
            };
            
            corrections.push(correction);
          });
        }
      });
    }

    return {
      resultOutput: response.revised || response.origin,
      corrections
    };
  }

  /**
   * Adjust corrections for chunk offset
   * @param result - Spell check result
   * @param chunk - Text chunk
   * @returns Adjusted result
   */
  private adjustCorrectionsForChunk(
    result: SpellCheckResult,
    chunk: TextChunk
  ): SpellCheckResult {
    return {
      ...result,
      corrections: result.corrections.map(correction => ({
        ...correction,
        position: correction.position ? {
          start: correction.position.start + chunk.startOffset,
          end: correction.position.end + chunk.startOffset
        } : undefined
      }))
    };
  }

  /**
   * Combine multiple spell check results
   * @param results - Array of results to combine
   * @returns Combined result
   */
  private combineResults(results: SpellCheckResult[]): SpellCheckResult {
    if (results.length === 0) {
      return { resultOutput: '', corrections: [] };
    }

    if (results.length === 1) {
      return results[0];
    }

    // Combine corrections from all results
    const allCorrections: Correction[] = [];
    let combinedOutput = '';

    results.forEach(result => {
      allCorrections.push(...result.corrections);
      combinedOutput += result.resultOutput;
    });

    // Remove duplicate corrections from overlapping regions
    const uniqueCorrections = this.removeDuplicateCorrections(allCorrections);

    return {
      resultOutput: combinedOutput,
      corrections: uniqueCorrections
    };
  }

  /**
   * Remove duplicate corrections
   * @param corrections - Array of corrections
   * @returns Deduplicated corrections
   */
  private removeDuplicateCorrections(corrections: Correction[]): Correction[] {
    const seen = new Map<string, Correction>();
    
    corrections.forEach(correction => {
      const key = `${correction.original}_${correction.position?.start}`;
      if (!seen.has(key)) {
        seen.set(key, correction);
      }
    });
    
    return Array.from(seen.values());
  }

  /**
   * Get API configuration
   * @param settings - Grammar check settings
   * @returns API configuration
   */
  private getApiConfig(settings: GrammarCheckSettings): ApiConfiguration {
    // Use custom API if provided, otherwise use default
    if (settings.apiKey && settings.apiHost) {
      return {
        apiKey: settings.apiKey,
        apiHost: settings.apiHost,
        apiPort: settings.apiPort || 443,
        protocol: settings.apiPort === 443 ? 'https' : 'http'
      };
    }
    
    return {
      ...DEFAULT_API_CONFIG,
      protocol: DEFAULT_API_CONFIG.apiPort === 443 ? 'https' : 'http'
    };
  }

  /**
   * Generate request key for deduplication
   * @param text - Text to generate key for
   * @returns Request key
   */
  private generateRequestKey(text: string): string {
    // Simple hash for request deduplication
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  /**
   * Maintain cache size
   */
  private maintainCacheSize(): void {
    if (this.morphemeCache.size > this.maxCacheSize) {
      const firstKey = this.morphemeCache.keys().next().value;
      if (firstKey !== undefined) {
        this.morphemeCache.delete(firstKey);
      }
    }
  }

  /**
   * Create delay promise
   * @param ms - Milliseconds to delay
   * @returns Promise that resolves after delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Handle API error response
   * @param status - HTTP status code
   * @param responseText - Response text
   * @returns Grammar check error
   */
  private handleApiError(status: number, responseText: string): GrammarCheckError {
    let type: GrammarCheckErrorType = 'API_ERROR' as GrammarCheckErrorType;
    let message = `API error: ${status}`;
    let retryable = false;
    let retryAfter: number | undefined;

    switch (status) {
      case 429:
        type = 'RATE_LIMIT' as GrammarCheckErrorType;
        message = 'Rate limit exceeded';
        retryable = true;
        retryAfter = 60000; // 1 minute
        break;
      case 401:
      case 403:
        message = 'Authentication failed';
        break;
      case 500:
      case 502:
      case 503:
        message = 'Server error';
        retryable = true;
        break;
      default:
        message = responseText || message;
    }

    return this.createError(type, message, retryable, status, retryAfter);
  }

  /**
   * Create grammar check error
   * @param type - Error type
   * @param message - Error message
   * @param retryable - Whether error is retryable
   * @param statusCode - HTTP status code
   * @param retryAfter - Retry after delay
   * @returns Grammar check error
   */
  private createError(
    type: GrammarCheckErrorType,
    message: string,
    retryable: boolean,
    statusCode?: number,
    retryAfter?: number
  ): GrammarCheckError {
    return {
      type,
      message,
      retryable,
      statusCode,
      retryAfter
    };
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.morphemeCache.clear();
  }
}

/**
 * API configuration interface
 */
interface ApiConfiguration {
  apiKey: string;
  apiHost: string;
  apiPort: number;
  protocol: 'http' | 'https';
}